import { Link } from "react-router";
import {
  ArrowRight,
  BarChart3,
  Camera,
  CheckSquare,
  FileText,
  Inbox,
} from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  PageHeader,
  Subtitle,
  VButton,
  VKpiCard,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import { useAssets } from "@/domains/assets";
import { useRequests, RequestStatusBadge } from "@/domains/requests";
import { useCases, CaseStatusBadge } from "@/domains/cases";
import { useTasks } from "@/domains/tasks";

export function ManagerDashboard(): React.JSX.Element {
  const user = useCurrentUser();
  const requests = useRequests();
  const cases = useCases();
  const assets = useAssets();
  const tasks = useTasks();

  const incomingRequests = (requests.data ?? []).filter(
    (r) => r.status === "submitted" || r.status === "inReview",
  );
  const activeCases = (cases.data ?? []).filter(
    (c) => c.status === "active" || c.status === "scheduled",
  );
  const today = "2026-05-11";
  const todayTasks = (tasks.data ?? []).filter(
    (t) => t.status !== "done" && t.dueDate === today,
  );
  const availableAssets = (assets.data ?? []).filter((a) => a.status === "available").length;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Goedemorgen, ${user.firstName}`}
        subtitle="Overzicht voor team functionele ondersteuning — Politiezone Antwerpen."
        actions={
          <VButton variant="outline" asChild>
            <Link to="/reporting">
              <BarChart3 className="size-4" aria-hidden />
              Rapportage
            </Link>
          </VButton>
        }
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <VKpiCard
          label="Inkomende aanvragen"
          value={incomingRequests.length}
          helper="Vereisen actie"
          icon={<Inbox className="size-4" aria-hidden />}
        />
        <VKpiCard
          label="Lopende dossiers"
          value={activeCases.length}
          helper="Actief of ingepland"
          icon={<FileText className="size-4" aria-hidden />}
        />
        <VKpiCard
          label="Taken vandaag te doen"
          value={todayTasks.length}
          helper="Openstaande taken"
          icon={<CheckSquare className="size-4" aria-hidden />}
        />
        <VKpiCard
          label="Beschikbare assets"
          value={availableAssets}
          helper="Direct inzetbaar"
          icon={<Camera className="size-4" aria-hidden />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card padding="lg" className="space-y-3">
          <div className="flex items-center justify-between">
            <CardTitle>Inkomende aanvragen</CardTitle>
            <Link to="/requests" className="text-sm font-medium text-primary-700 hover:underline">
              Alle aanvragen →
            </Link>
          </div>
          {incomingRequests.length === 0 ? (
            <Subtitle>Geen nieuwe aanvragen — alles is verwerkt.</Subtitle>
          ) : (
            <ul className="space-y-2">
              {incomingRequests.map((req) => (
                <li key={req.id}>
                  <Link
                    to={`/requests/${req.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 hover:bg-muted/40"
                  >
                    <div>
                      <Body weight="semibold">{req.title}</Body>
                      <Caption tone="muted">
                        {req.requester.firstName} {req.requester.lastName} · {req.period.startDate}
                      </Caption>
                    </div>
                    <RequestStatusBadge status={req.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padding="lg" className="space-y-3">
          <div className="flex items-center justify-between">
            <CardTitle>Lopende dossiers</CardTitle>
            <Link to="/cases" className="text-sm font-medium text-primary-700 hover:underline">
              Alle dossiers →
            </Link>
          </div>
          {activeCases.length === 0 ? (
            <EmptyState title="Geen lopende dossiers" />
          ) : (
            <ul className="space-y-2">
              {activeCases.slice(0, 6).map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/cases/${c.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 hover:bg-muted/40"
                  >
                    <div>
                      <Body weight="semibold">#{c.number} — {c.title}</Body>
                      <Caption tone="muted">
                        {c.address} · {c.assetIds.length} asset(s)
                      </Caption>
                    </div>
                    <CaseStatusBadge status={c.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padding="lg" className="space-y-3 lg:col-span-2">
          <div className="flex items-center justify-between">
            <CardTitle>Taken vandaag te doen</CardTitle>
            <Link to="/tasks" className="text-sm font-medium text-primary-700 hover:underline">
              Kalender →
            </Link>
          </div>
          {todayTasks.length === 0 ? (
            <Subtitle>Geen taken voor vandaag. Mooi opgekuist! ✨</Subtitle>
          ) : (
            <ul className="grid gap-2 md:grid-cols-2">
              {todayTasks.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
                >
                  <div className="min-w-0">
                    <Body weight="semibold" className="truncate">
                      {t.title}
                    </Body>
                    <Caption tone="muted">
                      {t.assignee ? t.assignee.name : "Niet toegewezen"} · #{t.caseId.replace("case-", "")}
                    </Caption>
                  </div>
                  <Link to={`/cases/${t.caseId}`} aria-label="Open dossier">
                    <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
